import React, { useState, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import AdminNavbar from "./AdminNavbar";
import AdminSidebar from "./AdminSidebar";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import Footer from "../layout/Footer";
import { toast } from "react-toastify";
import apiClient from "../../api/apiClient.js";

const AdminLayout = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isBackendConnected, setIsBackendConnected] = useState(true);
  const [isChecking, setIsChecking] = useState(true);

  // Vérifier la connectivité au backend
  useEffect(() => {
    const checkBackendConnectivity = async () => {
      try {
        // Vérifier que le backend est accessible et que l'API admin existe
        await apiClient.get("/health");
        
        // On essaie d'accéder à une route admin sans token
        // Ça devrait retourner 401, pas 404
        try {
          await apiClient.get("/admin/stats");
        } catch (error) {
          // 401 signifie que la route existe mais a besoin d'authentification (c'est bon)
          if (error.response && error.response.status === 401) {
            setIsBackendConnected(true);
          } else if (error.response && error.response.status === 404) {
            // 404 signifie que la route n'existe pas (problème)
            console.error("Endpoint admin non trouvé", error);
            toast.error("Impossible d'accéder au panneau d'administration: endpoint introuvable");
            setIsBackendConnected(false);
          } else {
            // Autre erreur
            console.error("Erreur lors de la vérification de l'API admin", error);
            setIsBackendConnected(false);
          }
        }
      } catch (error) {
        console.error("Erreur de connexion au backend", error);
        toast.error("Impossible de se connecter au serveur. Veuillez réessayer plus tard.");
        setIsBackendConnected(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkBackendConnectivity();
  }, []);

  // Gérer la taille de l'écran pour ajuster la sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    // Appliquer une seule fois au démarrage
    handleResize();

    // Ajouter un écouteur d'événement pour les changements de taille
    window.addEventListener("resize", handleResize);

    // Nettoyer l'écouteur
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Vérifier si l'utilisateur est administrateur
  useEffect(() => {
    if (!isLoading && (!user || user.role !== "admin")) {
      navigate("/login");
    }
  }, [user, isLoading, navigate]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  if (isLoading || isChecking) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner text="Chargement du panneau d'administration..." />
      </div>
    );
  }

  if (!isBackendConnected) {
    return (
      <div className="flex flex-col items-center justify-center h-screen p-4 text-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">Impossible d'accéder au service d'administration</h1>
        <p className="text-gray-600 dark:text-gray-300 mb-6">Le serveur d'administration semble inaccessible. Veuillez réessayer plus tard ou contacter le support technique.</p>
        <div className="flex gap-4">
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Réessayer
          </button>
          <button
            onClick={() => navigate("/")}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-gray-800 dark:text-gray-200 flex flex-col">
      <AdminNavbar toggleSidebar={toggleSidebar} />

      <AdminSidebar isOpen={sidebarOpen} />

      <main
        className={`transition-all duration-300 pt-16 ${sidebarOpen ? "md:ml-64" : "md:ml-20"} flex-grow`}
      >
        <div className="p-4 md:p-6">
          <Outlet />
        </div>
      </main>

      <div className={`${sidebarOpen ? "md:ml-64" : "md:ml-20"}`}>
        <Footer />
      </div>
    </div>
  );
};

export default AdminLayout;
